import { selectVehicle } from "action/garage/selectVehicle";
import { IPageContent } from "api/interfaces/static-content.type";
import PagePlaceholder, { PLACEHOLDER_PAGE_TYPE } from "component/category/PagePlaceholder";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import VehicleSelectorSelected from "component/product/V2/VehicleSelectorSelected";
import ProductImageSection from "component/product/V2/image-section/ProductImageSection";
import ProductOverview from "component/product/V2/overview/ProductOverview";
import { getTotalFilters } from "component/product/product-container-util";
import EmbedVideo from "component/search/embed-video";
import BoxedVehicleSelector from "component/vehicle-form/BoxedVehicleSelector";
import { MAKE_MODEL_YEAR, YEAR_MAKE_MODEL } from "component/vehicle-form/constants";
import { LAYOUT } from "constant/layout-constant";
import { tier0s } from "constant/shared-constants";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { IVehicle } from "interfaces/session/IVehicle";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import qs from "query-string";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { getProductHeading } from "util/product/get-product-heading";
import { PAGE_TYPES } from "util/product/product-constants";
import getFocusEl from "util/vehicle/getFocusEl";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import productStyles from "../product.module.scss";
import styles from "./LinePageSection.module.scss";
import { getLinePageProductImagePath, getLinePageThumbImagePath } from "./utils";
const SsrProductContainerCompact = dynamic(
  () => import(/* webpackChunkName: "ssr-product-container-compact" */"component/category/SsrProductContainerCompact"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
);
const SsrProductContainerRegular = dynamic(
  () => import(/* webpackChunkName: "ssr-product-container-regular" */"component/category/SsrProductContainerRegular"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
);

interface IProps{
  staticContent: IPageContent;
}

function LinePageSection(props: IProps) {
  const {
    pageType,
    details,
    productReviews,
    productQuestions,
    components,
    staticContent,
    productsAggregation,
    maximumPrice,
    minimumPrice,
    make,
    model,
    fitmentOptionsDetails,
    tiersWithUrls,
  } = props;

  let { categoryAggregation } = productsAggregation;

  // Fallback for Category Aggregation if backend does not return category aggregation
  if (!categoryAggregation || Object.keys(categoryAggregation).length === 0 || categoryAggregation?.tier0s?.length === 0) {
    categoryAggregation = {
      tier0s: tier0s.map((el) => ({ ...el, bucket: el.name, pageType: "TIER0_PAGE" })),
    };
  }

  const url = "/[pathname]";
  const router = useRouter();
  const [isMobile, , isDesktop] = useScreenSize();

  const { selectedVehicle, defaultValues } = useSelector((state: RootState) => state.garage);
  const { t } = useTranslation(["product", "common"]);
  const commonT = (_: string) => t(_, { ns: "common" });
  const dispatch = useDispatch();
  const { currentPageType } = useSelector((store: RootState) => store.utils);

  const {
    RatingUI,
  } = components;
  const { pageDetails = {}, productLineImages = [] } = staticContent;
  const {
    manufacturerImageUrl: manufactureImageUrl, manufacturer, productLineId,
    pageDisplayableTitle = "",
  } = pageDetails;

  const { products: productList, ...filterOptions } = productsAggregation;
  const query = process.browser ? qs.parse(location.search) : router.query;
  const urlInformation = {};
  const appliedFilters = getTotalFilters(
    commonT,
    query,
    urlInformation,
    selectedVehicle,
    {},
  );
  const pageTitle = getProductHeading(undefined, pageDetails);
  const thumbnails = (productLineImages.find((_: any) => _.type === "SINGLE_IMAGE")?.urls || []).filter((_: any) => _ !== "");
  let productOverviewSection: any = null;

  const selectionOrder = currentPageType === PAGE_TYPES.MAKE_MODEL_PRODUCT_LINE_PAGE ? MAKE_MODEL_YEAR : YEAR_MAKE_MODEL;
  const defaultVehicle = {
    defaultMake: defaultValues.defaultMake || make,
    defaultModel: defaultValues.defaultModel || model,
    defaultYear: defaultValues.defaultYear,
  };

  const focusElement = getFocusEl(selectionOrder, defaultVehicle);
  const productOverviewSectionRef = (element: any) => {
    productOverviewSection = element;
  };
  const { videos = [] } = staticContent;

  const propsToPass = {
    defaultLayout: LAYOUT.COMPACT,
    productList,
    filterOptions,
    url,
    pageType,
    appliedFilters,
    staticContent,
    pageDisplayableTitle: !selectedVehicle ? pageDisplayableTitle
      : `${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model} ${pageDisplayableTitle}`,
    filterParams: {
      manufacturers: {
        enabled: false,
      },
      fitments: {
        enabled: false,
      },
    },
    fitmentOptionsDetails,
    categoryAggregation,
    tiersWithUrls,
  };
  const handleProductOverviewSectionExpand = (section: any) => {
    if (productOverviewSection) {
      productOverviewSection.expandSection(section);
    }
  };

  return (
    <div>
      <HideIf show={Object.keys(staticContent).length}>
        <div className="row">
          <div className={`col-xs-12 col-md-6 d-flex flex-column ${productStyles.sticky_left_product}`}>
            <ProductImageSection
              {...{
                thumbnail: thumbnails[0],
                description: pageTitle,
                manufacturer,
                manufactureImageUrl,
                imageTitle: `${pageTitle} by ${manufacturer}`,
                productImages: productLineImages,
                getThumbImagePath: getLinePageThumbImagePath,
                getProductImagePath: getLinePageProductImagePath,
                maximumPrice,
                minimumPrice,
                isRegularDesktop: isDesktop,
              }}
              staticContent={staticContent}
            />
          </div>

          <div className={`col-xs-12 col-md-6 ${styles.margin_productDelivery_linePage}`}>

            <RatingUI
              {...{
                PRODUCT_ID: productLineId,
              }}
            />

            <PAText component="h3" bold fontSize={FONT_SIZE[2]} className={styles.line_page_fitment_title}>{t("Check If it fits")}</PAText>
            {!validateVehicle(selectedVehicle)
          && (
            // this is mainly for SEO, page inter linking purposes
            <div className={styles.vehicle_selector_container}>

              <BoxedVehicleSelector
                focusEl={focusElement}
                onSelectVehicle={(newVehicle: IVehicle) => {
                  dispatch(selectVehicle(newVehicle));
                }}
                selectionOrder={selectionOrder}
                {...defaultVehicle}
                dropdownOpenFromRight
              />
            </div>
          )}

            <HideIf show={selectedVehicle}>
              <VehicleSelectorSelected selectedVehicle={selectedVehicle} />
            </HideIf>

            <ProductOverview
              {...{
                details,
                productReviews,
                productQuestions,
                staticContent,
                handleProductOverviewSectionExpand,
                manufacturer,
                manufactureImageUrl,
                maximumPrice,
                minimumPrice,
                getThumbImagePath: getLinePageThumbImagePath,
              }}
              thumbnail={thumbnails[0]}
              description={pageTitle}
              ref={productOverviewSectionRef}
            />

          </div>
        </div>
      </HideIf>

      <div id="PRODUCTS" className="row">
        {(isMobile)
          ? (
            <SsrProductContainerCompact {...propsToPass} />
          )
          : (
            <div className="col-xs-12">
              <SsrProductContainerRegular {...propsToPass} />
            </div>
          )}
      </div>

      <div className={styles.youtube_video}>
        {videos.map((video) => <EmbedVideo key={video.vimeoId || video.youtubeId} showThumbNail={false} videoSrc={video.vimeoId ? `https://player.vimeo.com/video/${video.vimeoId}` : `https://www.youtube.com/embed/${video.youtubeId}?wmode=transparent&rel=0&showinfo=0&fs=1`} />)}
      </div>
    </div>

  );
}

export default LinePageSection;
